import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Inject,
  Optional,
  signal,
  ViewChild,
} from '@angular/core';
import { FormlyModule } from '@ngx-formly/core';
import { MatInputModule } from '@angular/material/input';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { catchError, of, takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FuseAlertComponent } from '../../../../@fuse/components/alert';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { UtilityService } from '../../services/utility.service';
import { UserStore } from 'app/core/services/user.store';
import { TitleService } from '../../services/title.service';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { ClearableFieldType } from './clearable-field.type';
import { FieldLabelRowComponent } from './field-label-row.component';
import type { Entry, Module } from '../../../features/admin/modules/modules.model';
import { EntriesLoadErrorAlertComponent } from '../entries-load-error-alert/entries-load-error-alert.component';
import { entryDisplayName, LinkageEntriesSearch } from './services/linkage-entries-search';

interface LinkageTag {
  linkageEntryId: string;
  entryId: string;
  label: string;
}

class DialogData {}

@Component({
  selector: 'formly-linkage-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    FormlyModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    MatProgressSpinner,
    FuseAlertComponent,
    FormlyDataCyDirective,
    ClearFieldButtonComponent,
    FieldLabelRowComponent,
    EntriesLoadErrorAlertComponent,
  ],
  template: `
    <div>
      <mat-form-field class="w-full" appearance="outline" floatLabel="always">
        <mat-label>
          <app-field-label-row [label]="props.label" [required]="props.required" [info]="props.info"></app-field-label-row>
        </mat-label>
        <input
          matInput
          type="text"
          #searchInput
          [formControl]="searchControl"
          [matAutocomplete]="auto"
          [placeholder]="!linkedEntryId() || !canMutate() ? '' : (props.placeholder ?? 'Zoeken...')"
          [readonly]="!canMutate()"
          [formlyDataCy]="field"
          (focus)="onSearchFocus()" />
        @if (isSearching()) {
          <span matSuffix class="linkage-select-suffix-status">
            <mat-spinner diameter="20"></mat-spinner>
          </span>
        }
        <!-- Mounted for every value state on purpose. The compact 37px field geometry in
             _forms.scss keys off :has(app-clear-field-button), so conditionally mounting this host
             made the field height jump between 37px and Material's default on every search debounce
             and on every clear. The primitive already renders nothing unless it has a clearable
             value, so hasValue carries the condition instead. -->
        <app-clear-field-button
          matSuffix
          [field]="field"
          [hasValue]="!!selectedTag() && !isSearching()"
          [disabled]="isDisabled()"
          [readOnly]="isReadOnly()"
          ariaLabel="Verwijder koppeling"
          [focusTarget]="searchInput"
          (clearSelection)="clearSelection()" />
        <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayFn" (optionSelected)="onOptionSelected($event)">
          @for (option of searchResults(); track option.value.id) {
            <mat-option [value]="option.value">{{ option.label }}</mat-option>
          }
        </mat-autocomplete>
        @if (field.props.description) {
          <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
        }
      </mat-form-field>
      <!-- A 403 on the search must not read as "no results" — see entries-load-error.ts. -->
      @if (loadError()) {
        <app-entries-load-error-alert class="my-2" [error]="loadError()!" [label]="props.label" dataCyPrefix="linkage-select" />
      }
      @if (!linkedEntryId()) {
        <fuse-alert class="my-2" [type]="'warning'">
          Je kan pas een koppeling maken als je huidige item hebt opgeslagen.
        </fuse-alert>
      }
    </div>
  `,
  styles: `
    .linkage-select-suffix-status {
      display: inline-flex;
      flex: 0 0 24px;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
    }
  `,
})
export class FormlyLinkageSelectComponent extends ClearableFieldType implements AfterViewInit {
  readonly #modulesStore = inject(ModulesStore);
  readonly #entriesService = inject(EntriesService);
  readonly #userStore = inject(UserStore);
  readonly #titleService = inject(TitleService);
  readonly #destroy$ = injectDestroy();

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  route = inject(ActivatedRoute);
  paramsParent;
  params = toSignal(this.route.paramMap);

  searchControl = new FormControl<string | Entry | null>(null);

  linkageEntries = signal<Entry[]>([]);
  readonly #pendingSelection = signal<LinkageTag | null>(null);

  readonly #search = new LinkageEntriesSearch({
    entriesService: this.#entriesService,
    destroy$: this.#destroy$,
    namespace: 'LinkageSelect',
    searchModule: () => this.searchModule(),
  });
  isSearching = this.#search.isSearching;
  searchResults = this.#search.searchResults;
  loadError = this.#search.loadError;

  currentModule = computed(() => {
    if (this.paramsParent() === undefined) {
      return this.data['entity'];
    }
    if (this.paramsParent()?.get('name') === 'bedrijfsinstellingen') {
      return this.#modulesStore.enabledModules().find(module => module.slug === 'companies');
    }
    if (this.paramsParent()?.get('name') === 'profiel') {
      return this.#modulesStore.enabledModules().find(module => module.slug === 'users');
    }
    return this.#modulesStore.enabledModules().find(module => module.slug === this.paramsParent()?.get('name'));
  });

  currentEntry = computed(() => {
    if (this.params()?.get('id') === undefined) {
      return this.data['model'];
    }
    if (this.options.formState?.entry != null) {
      return this.options.formState.entry;
    }
    return this.#modulesStore.entries()[this.currentModule()?.id]?.find(entry => {
      return entry.id === this.params()?.get('id');
    });
  });

  linkedEntryId = computed(() => {
    if (this.options.formState?.entry?.id != null) {
      return this.options.formState.entry.id;
    } else if (this.#titleService.getTitle().toLowerCase() === 'profiel') {
      return this.#userStore.currentUserId();
    } else if (this.data != undefined && this.data['model']?.id) {
      return this.data['model']?.id;
    } else {
      return this.params()?.get('id');
    }
  });

  linkedModule = computed(() => {
    if (!this.props.linkedModule) return undefined;
    return this.#modulesStore.enabledModules().find(module => module.code === this.props.linkedModule);
  });

  searchModule = computed(() => {
    if (!this.props.searchModuleCode) return undefined;
    return this.#modulesStore.enabledModules().find(module => module.code === this.props.searchModuleCode);
  });

  selectedTag = computed<LinkageTag | null>(() => {
    const pending = this.#pendingSelection();
    if (pending) return pending;
    for (const entry of this.linkageEntries()) {
      const tag = this.extractTag(entry);
      if (tag) return tag;
    }
    return null;
  });

  constructor(@Optional() @Inject(MAT_DIALOG_DATA) public data: DialogData) {
    super();
    if (this.route?.parent?.paramMap) {
      this.paramsParent = toSignal(this.route?.parent?.paramMap);
    }

    // Keep the input value in sync with the actual selected linkage.
    // Sets the input text to the current selection label (or clears it) without
    // triggering a search.
    effect(() => {
      const tag = this.selectedTag();
      const displayEntry: Entry | null = tag ? ({ id: tag.entryId, selectedInput: tag.label } as Entry) : null;
      const currentValue = this.searchControl.value;
      if (typeof currentValue === 'object' && currentValue?.id === displayEntry?.id) return;
      this.searchControl.setValue(displayEntry, { emitEvent: false });
    });
  }

  ngAfterViewInit() {
    requestAnimationFrame(() => {
      this.loadLinkedEntries();
      this.#search.connect(this.searchControl);
    });
  }

  onSearchFocus() {
    if (!this.canMutate()) return;
    // Select existing text on focus so the user can type to replace it.
    if (this.searchInput) {
      this.searchInput.nativeElement.select();
    }
    if (this.searchModule() && this.searchResults().length === 0) {
      this.#search.searchOnFocus();
    }
  }

  displayFn = (value: Entry | string | null): string => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.selectedInput ?? '';
  };

  onOptionSelected(event: { option: { value: Entry } }) {
    if (!this.canMutate()) return;
    const selectedEntry = event.option.value;
    const current = this.selectedTag();

    if (current?.entryId === selectedEntry.id) return;

    // Remove the existing linkage (if any).
    if (current?.linkageEntryId && !current.linkageEntryId.startsWith('temp-')) {
      this.removeSelection(current);
    }

    this.createLinkage(selectedEntry);
  }

  clearSelection() {
    if (!this.canMutate()) return;
    const current = this.selectedTag();
    if (current?.linkageEntryId && !current.linkageEntryId.startsWith('temp-')) {
      this.removeSelection(current);
    }
    this.#pendingSelection.set(null);
    // clear() owns the whole reset (control, results, error, in-flight request, term
    // memory) — see the LinkageEntriesSearch contract for why it must not emit.
    this.#search.clear();
  }

  private removeSelection(tag: LinkageTag) {
    if (!this.canMutate()) return;
    const linkageModule = this.linkedModule();
    if (!linkageModule?.id || !tag.linkageEntryId) return;

    this.#modulesStore.deleteEntry({
      moduleId: linkageModule.id,
      entryId: tag.linkageEntryId,
    });

    this.linkageEntries.update(entries => entries.filter(e => e.id !== tag.linkageEntryId));
  }

  private loadLinkedEntries() {
    const entryId = this.linkedEntryId();
    const linkageModule = this.linkedModule();
    if (!entryId || !linkageModule?.id) return;

    const fieldA = this.props.linkedFieldA;
    const fieldB = this.props.linkedFieldB;
    if (!fieldA || !fieldB) return;

    const filterA = `${UtilityService.dotPathToObjectPath(fieldA + '.id')} eq '${entryId}'`;
    const filterB = `${UtilityService.dotPathToObjectPath(fieldB + '.id')} eq '${entryId}'`;

    const moduleFilterId = this.getModuleFilterId() ?? this.searchModule()?.id;
    let filter: string;

    if (moduleFilterId) {
      const moduleIdPathA = UtilityService.dotPathToObjectPath(fieldA.split('.').slice(0, -1).join('.') + '.moduleId');
      const moduleIdPathB = UtilityService.dotPathToObjectPath(fieldB.split('.').slice(0, -1).join('.') + '.moduleId');
      filter = `(${filterA} and ${moduleIdPathB} eq '${moduleFilterId}') or (${filterB} and ${moduleIdPathA} eq '${moduleFilterId}')`;
    } else {
      filter = `${filterA} or ${filterB}`;
    }

    this.#entriesService
      .getEntries({
        moduleId: linkageModule.id,
        offset: 0,
        limit: 1000,
        filter,
      })
      .pipe(takeUntil(this.#destroy$))
      .subscribe(entries => {
        this.linkageEntries.set(entries);
      });
  }

  private extractTag(linkageEntry: Entry): LinkageTag | undefined {
    const otherItem = this.getOtherSideItem(linkageEntry);
    const otherEntry = otherItem?.entry;
    if (!otherEntry?.id) return undefined;

    // Only show selections where the other side belongs to this field's searchModule.
    const searchModId = this.searchModule()?.id;
    if (searchModId && otherItem?.moduleId && otherItem.moduleId !== searchModId) {
      return undefined;
    }

    return {
      linkageEntryId: linkageEntry.id,
      entryId: otherEntry.id,
      label: otherEntry.selectedInput || 'Onbekend',
    };
  }

  private getOtherSideItem(
    linkageEntry: Entry
  ): { moduleId?: string; entry?: { id?: string; selectedInput?: string } } | undefined {
    if (!linkageEntry?.entry) return undefined;

    const fieldAParts = this.props.linkedFieldA?.split('.') ?? [];
    const fieldBParts = this.props.linkedFieldB?.split('.') ?? [];
    const itemAKey = fieldAParts[1];
    const itemBKey = fieldBParts[1];

    const itemAPath = fieldAParts.slice(1);
    const itemBPath = fieldBParts.slice(1);

    const entryA = this.getNestedValue(linkageEntry.entry, itemAPath);
    const entryB = this.getNestedValue(linkageEntry.entry, itemBPath);

    if (entryA?.id === this.linkedEntryId()) {
      return linkageEntry.entry[itemBKey];
    }
    return linkageEntry.entry[itemAKey];
  }

  private getNestedValue(obj: any, path: string[]): any {
    return path.reduce((current, key) => current?.[key], obj);
  }

  private getModuleFilterId(): string | undefined {
    const defaults = this.props.defaultValuesLinkedItems;
    if (!defaults?.length) return undefined;

    const moduleDefault = defaults.find(item => item.targetField?.endsWith('moduleId'));
    if (!moduleDefault) return undefined;

    switch (moduleDefault.basedOn) {
      case 'moduleId':
        return this.currentModule()?.id;
      case 'static':
        return moduleDefault.value;
      default:
        return undefined;
    }
  }

  private createLinkage(selectedEntry: Entry) {
    if (!this.canMutate()) return;
    const linkageModule = this.linkedModule();
    const searchMod = this.searchModule();
    const currentEntry = this.currentEntry();
    if (!linkageModule?.id || !currentEntry?.id || !searchMod) return;

    const fieldA = this.props.linkedFieldA;
    const fieldB = this.props.linkedFieldB;
    if (!fieldA || !fieldB) return;

    const selectedLabel = selectedEntry.selectedInput ?? entryDisplayName(selectedEntry, searchMod);

    // Optimistic: show the selection immediately with a temporary ID.
    const tempId = `temp-${Date.now()}`;
    this.#pendingSelection.set({
      linkageEntryId: tempId,
      entryId: selectedEntry.id,
      label: selectedLabel,
    });
    this.linkageEntries.update(entries => [...entries, { id: tempId, entry: null } as Entry]);

    const newEntry: Entry = {
      id: undefined,
      entry: { archived: false },
    };

    // Side A: current entry.
    UtilityService.setKeyValuePath(newEntry, fieldA, {
      id: currentEntry.id,
      selectedInput: currentEntry.selectedInput ?? this.getCurrentEntryDisplayName(),
    });
    const moduleIdPathA = fieldA.split('.').slice(0, -1).join('.') + '.moduleId';
    UtilityService.setKeyValuePath(newEntry, moduleIdPathA, this.currentModule()?.id);

    // Side B: selected entry.
    UtilityService.setKeyValuePath(newEntry, fieldB, {
      id: selectedEntry.id,
      selectedInput: selectedLabel,
    });
    const moduleIdPathB = fieldB.split('.').slice(0, -1).join('.') + '.moduleId';
    UtilityService.setKeyValuePath(newEntry, moduleIdPathB, searchMod.id);

    // Apply additional default values.
    const defaults = this.props.defaultValuesLinkedItems;
    if (defaults?.length) {
      for (const item of defaults) {
        if (item.targetField?.endsWith('moduleId')) continue;
        let value: any;
        switch (item.basedOn) {
          case 'moduleId':
            value = this.currentModule()?.id;
            break;
          case 'static':
            value = item.value;
            break;
          case 'entry':
            value = item.sourceField ? UtilityService.keyPathToValue(currentEntry, item.sourceField) : undefined;
            break;
        }
        if (value !== undefined) {
          UtilityService.setKeyValuePath(newEntry, item.targetField, value);
        }
      }
    }

    this.#entriesService
      .addEntry(linkageModule.id, newEntry.entry)
      .pipe(
        takeUntil(this.#destroy$),
        catchError(e => {
          console.error('Failed to create linkage:', e);
          // Rollback: remove the optimistically added selection.
          this.#pendingSelection.update(current => (current?.linkageEntryId === tempId ? null : current));
          this.linkageEntries.update(entries => entries.filter(entry => entry.id !== tempId));
          return of(null);
        })
      )
      .subscribe(result => {
        if (result) {
          // Replace the temporary selection with the real data.
          this.#pendingSelection.update(current => (current?.linkageEntryId === tempId ? null : current));
          this.linkageEntries.update(entries => [...entries.filter(entry => entry.id !== tempId), result]);
        }
      });
  }

  private getCurrentEntryDisplayName(): string {
    const entry = this.currentEntry();
    // '' and no id-fallback: a GUID is not a usable label for the current-entry side.
    return entry ? entryDisplayName(entry, this.currentModule(), { fallback: '', idFallback: false }) : '';
  }

  canMutate(): boolean {
    return !this.isDisabled() && !this.isReadOnly();
  }
}
